import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  Button,
  Alert,
} from '@kollektive/design-system';
import type { ExpiryOption } from '@/types';
import styles from './GenerateTokenModal.module.css';

interface GenerateTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (name: string, expiry: ExpiryOption) => Promise<void>;
  isLoading: boolean;
}

const EXPIRY_OPTIONS = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
  { value: 'never', label: 'Never' },
];

export function GenerateTokenModal({
  isOpen,
  onClose,
  onGenerate,
  isLoading,
}: GenerateTokenModalProps) {
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState<ExpiryOption>('90d');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Token name is required');
      return;
    }

    if (name.length > 100) {
      setError('Token name must be 100 characters or less');
      return;
    }

    try {
      await onGenerate(name.trim(), expiry);
      setName('');
      setExpiry('90d');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setExpiry('90d');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Generate Token" onClose={handleClose} />
        <ModalBody>
          <div className={styles.formFields}>
            <Input
              label="Token Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Claude Desktop"
              maxLength={100}
              disabled={isLoading}
              autoFocus
            />

            <Select
              label="Expiration"
              options={EXPIRY_OPTIONS}
              value={expiry}
              onChange={(value) => setExpiry(value as ExpiryOption)}
              disabled={isLoading}
            />

            {error && (
              <Alert variant="error" styleVariant="glass">
                {error}
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !name.trim()}
          >
            {isLoading ? 'Generating...' : 'Generate'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
